import type { OeeGroupKey, OeePoint } from "./types";

/**
 * Deterministic mock dataset (no CubeJS backend for this widget): 2 years of
 * daily OEE readings — weekday dips, a seasonal wave and seeded jitter, so
 * the data is stable across reloads. Swapping in the real API is a body-only
 * change in `thunks.ts`.
 */

const DAY_MS = 86_400_000;
/** Fake dataset span: 2 years of daily records. */
const TOTAL_DAYS = 730;

/** Deterministic PRNG (LCG) so the fake data is stable across renders and loads. */
function makeRand(seed = 20260918) {
  let s = seed % 2147483647;
  if (s <= 0) s += 2147483646;
  return () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

const pad = (n: number) => String(n).padStart(2, "0");

function bucketKey(date: Date, group: OeeGroupKey): string {
  if (group === "month") return `${date.getFullYear()}-${pad(date.getMonth() + 1)}`;
  if (group === "week") {
    // Week starting Monday, labeled by its start date.
    const monday = new Date(date);
    monday.setDate(date.getDate() - ((date.getDay() + 6) % 7));
    return `${monday.getFullYear()}-${pad(monday.getMonth() + 1)}-${pad(monday.getDate())}`;
  }
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

type OeeMetrics = Omit<OeePoint, "date">;

function makeDailyMetrics(date: Date, rand: () => number): OeeMetrics {
  const dow = date.getDay();
  const weekday = dow === 0 || dow === 6 ? 0.7 : 1; // quieter weekends
  const season = 1 + 0.08 * Math.sin((2 * Math.PI * date.getTime()) / (365 * DAY_MS));
  const jitter = () => 0.9 + rand() * 0.2;
  const availability = Math.min(100, 88 * weekday * season * jitter());
  const performance = Math.min(100, 84 * weekday * season * jitter());
  const quality = Math.min(100, 95 * weekday * jitter());
  return {
    availability: Math.round(availability * 10) / 10,
    performance: Math.round(performance * 10) / 10,
    quality: Math.round(quality * 10) / 10,
    oee: Math.round(((availability / 100) * (performance / 100) * (quality / 100)) * 1000) / 10,
  };
}

interface RawOeeRecord {
  date: Date;
  availability: number;
  performance: number;
  quality: number;
  oee: number;
}

/** Full daily series, oldest → newest (unaggregated, no Redux/API imports). */
function generateDailyRecords(): RawOeeRecord[] {
  const rand = makeRand();
  const end = new Date();
  end.setHours(0, 0, 0, 0);
  const records: RawOeeRecord[] = [];
  for (let i = TOTAL_DAYS - 1; i >= 0; i--) {
    const date = new Date(end.getTime() - i * DAY_MS);
    records.push({ date, ...makeDailyMetrics(date, rand) });
  }
  return records;
}

/** Full grouped series for the requested granularity, oldest → newest. */
export function generateOeeSeries(group: OeeGroupKey): OeePoint[] {
  const records = generateDailyRecords();
  if (group === "day") {
    return records.map((r) => ({
      date: bucketKey(r.date, "day"),
      availability: r.availability,
      performance: r.performance,
      quality: r.quality,
      oee: r.oee,
    }));
  }

  const groups = new Map<string, OeePoint & { days: number }>();
  for (const record of records) {
    const key = bucketKey(record.date, group);
    let g = groups.get(key);
    if (!g) {
      g = { date: key, availability: 0, performance: 0, quality: 0, oee: 0, days: 0 };
      groups.set(key, g);
    }
    g.availability += record.availability;
    g.performance += record.performance;
    g.quality += record.quality;
    g.days += 1;
  }
  return [...groups.values()].map(({ days, ...g }) => {
    // Averages per bucket (percentages must not be summed across days).
    const availability = Math.round((g.availability / days) * 10) / 10;
    const performance = Math.round((g.performance / days) * 10) / 10;
    const quality = Math.round((g.quality / days) * 10) / 10;
    return {
      date: g.date,
      availability,
      performance,
      quality,
      oee: Math.round(((availability / 100) * (performance / 100) * (quality / 100)) * 1000) / 10,
    };
  });
}
